import {toast} from "react-toastify"
import {validator} from "./answer-validations"
import {fillConnections} from "./tree-functions"
import {selectTenantTree} from "./tenant-functions"

export const DEFAULT_TENANT_ID = "teckpro"
export const MULTITENANT_TREES = ["themenfelder", "questionList", "produkte", "anlassbaum"]

export const errorParser = (updateData, root) => (response) => {
    const parsedResponse = mapMessages(response._messages, root)
    updateData(parsedResponse, "/messages")
    return Promise.resolve()
}

export const getLane = widget => widget?.lane?.origin

export const getQuestionsFromQuestionaire = questionaire => questionaire?.connections?.map(entry => entry?.origin)[0]?.children

export const setPxSuffix = (number) => `${number}px`

export const getEventValue = (event) => event.target.value

export const getImageForPath = (path) =>
    window._env_.ARBEITSKONSOLE_SERVER_URL + path

export const fileServerUrlParser = (url) =>
    url.startsWith("/") ? window._env_.ARBEITSKONSOLE_SERVER_URL + url : url

export const backendBaseUrl = globalThis._env_.BACKEND_API_URL + "/api"
export const uwpBackendBaseUrl = globalThis._env_.UWPPLUS_BACKEND_API_URL + "/api"

export const pathBuilder = (hallink) => hallink.href.split("/api")[1]

const BACKOFFICE_WINDOW_NAME = "tom-backoffice"
export const SELECT_NODE_MESSAGE = "tom-select-node"
export const SELECT_NODE_ACK = "tom-select-node-ack"
const SELECT_NODE_ACK_TIMEOUT_MS = 700

let backofficeWindow = null
let ackListenerAttached = false
let ackTimeoutId = null

const backofficeOrigin = () =>
    new URL(globalThis._env_.TOM_BACKOFFICE_URL).origin

const backofficeUrl = (nodeId) => {
    const base = globalThis._env_.TOM_BACKOFFICE_URL
    if (!nodeId) return base
    const separator = base.endsWith("/") ? "" : "/"
    return `${base}${separator}?tomNode=${encodeURIComponent(nodeId)}`
}

const clearAckTimeout = () => {
    if (!ackTimeoutId) return
    clearTimeout(ackTimeoutId)
    ackTimeoutId = null
}

const ensureAckListener = () => {
    if (ackListenerAttached) return
    ackListenerAttached = true
    globalThis.addEventListener("message", (event) => {
        if (event.origin !== backofficeOrigin()) return
        if (event.data?.type !== SELECT_NODE_ACK) return
        clearAckTimeout()
    })
}

export const openTomBackoffice = (nodeId) => {
    const url = backofficeUrl(nodeId)
    ensureAckListener()

    if (!backofficeWindow || backofficeWindow.closed) {
        backofficeWindow = globalThis.open(url, BACKOFFICE_WINDOW_NAME)
        return backofficeWindow
    }

    backofficeWindow.focus()
    if (!nodeId) return backofficeWindow

    clearAckTimeout()
    backofficeWindow.postMessage(
        {type: SELECT_NODE_MESSAGE, nodeId},
        backofficeOrigin(),
    )
    ackTimeoutId = setTimeout(() => {
        ackTimeoutId = null
        if (backofficeWindow && !backofficeWindow.closed) {
            backofficeWindow.location.href = url
        }
    }, SELECT_NODE_ACK_TIMEOUT_MS)
    return backofficeWindow
}

const mapMessages = (messages, root) => {
    const result = {}

    if (!Array.isArray(messages)) {
        return result
    }

    messages.forEach((message) => {
        const {severity, paths, text} = message

        if (!severity || !paths || !text) {
            return
        }

        let currentLevel = result
        const severityLowerCase = severity.toLowerCase()

        if (!currentLevel[severityLowerCase]) {
            currentLevel[severityLowerCase] = {}
        }
        currentLevel = currentLevel[severityLowerCase]
        if (!paths.length) {
            toast.error(text, {
                toastId: text
            })
        } else {
            paths.forEach((path) => {
                const rerootedPath = path.replace("_data", root?.substring(1))
                const pathParts = rerootedPath.split(".")
                pathParts.forEach((part, index) => {
                    if (index === pathParts.length - 1) {
                        currentLevel[part] = text
                        if (text) {
                            toast.error(text, {
                                toastId: text
                            })
                        }
                    } else {
                        if (!currentLevel[part]) {
                            currentLevel[part] = {}
                        }
                        currentLevel = currentLevel[part]
                    }
                })
            })
        }
    })

    return result
}

export const removeNullValues = (object) => {
    for (const key in object) {
        if (object[key] === null) {
            delete object[key]
        }
    }
    return object
}

export const deNumberStringToNumber = (deNumberString) =>
    parseFloat(
        (typeof deNumberString === "string" ? deNumberString : "")
            .replaceAll(".", "")
            .replaceAll(",", "."),
    )

export const doNothing = () => {}

export const numberToDeNumberString = (number) =>
    (Number.isFinite(number) ? number : 0).toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })

export const safeParseStringToNumber = (value) =>
    value ? deNumberStringToNumber(value) : 0

export const dateStringToNumber = (dateString) =>
    ("" + dateString)?.replaceAll("-", "")

/** PRODUKTFINDER */

export const decimalToPercent = (decimal) => decimal * 100

const calculateGesamtgewicht = (produkt, merkmalList) => {
    return merkmalList
        .map((merkmal) => Number(produkt.merkmale?.[merkmal.id]?.gewicht ?? 0))
        ?.reduce((gesamt, merkmalGewicht) => gesamt + merkmalGewicht, 0)
}

export const calculateMatchPercentage = (
    produkt,
    searchParameter,
    merkmalliste,
) => {
    const erfuellteMerkmale = merkmalliste.filter((merkmal) =>
        validator(
            searchParameter?.kunde?.[merkmal.id],
            produkt.merkmale?.[merkmal.id],
        ),
    )
    const gewichtErfuellteMerkmale = calculateGesamtgewicht(
        produkt,
        erfuellteMerkmale,
    )
    const gesamtGewicht = calculateGesamtgewicht(produkt, merkmalliste)
    return decimalToPercent(gewichtErfuellteMerkmale / gesamtGewicht)
}

export const debounce = (func, delay = 500) => {
    let timeoutId
    return (...args) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
            func(...args)
        }, delay)
    }
}

export const getQueryParam = (param) => {
    const fullHash = window.location.href
    const route = fullHash.substring(1)
    const queryStartIndex = route.indexOf("?")

    let queryString = ""
    if (queryStartIndex !== -1) {
        queryString = route.substring(queryStartIndex)
    } else {
        return ""
    }

    const params = new URLSearchParams(queryString)
    return params.get(param)
}

export const evaluateFullHeight = (fullHeigth) => (fullHeigth ? "100%" : "auto")

export const findNodeById = (id, nodeList) => {
    if (!id) {
        return null
    }
    const result = findNodeAndSiblingsById(id, nodeList)
    return result ? result[0] : null
}

const findNodeAndSiblingsById = (id, nodeList) => {
    if (Array.isArray(nodeList)) {
        for (const node of nodeList) {
            if (node.id === id) {
                return [node, nodeList]
            }
            const found = findNodeAndSiblingsById(id, node.children)

            if (found) {
                return found
            }
        }
    }
}

export const stripOrigins = (value) => {
    if (Array.isArray(value)) return value.map(stripOrigins)
    if (value && typeof value === "object") {
        const result = {}
        for (const [key, v] of Object.entries(value)) {
            if (key === "origin") continue
            result[key] = stripOrigins(v)
        }
        return result
    }
    return value
}

export const resolveConnectionsForAnwenungImport = (
    exportedData,
    {tid = DEFAULT_TENANT_ID, multiTenantTrees = MULTITENANT_TREES} = {},
) => {
    const data = {...exportedData.data}
    for (const treeName of multiTenantTrees) {
        if (data[treeName] !== undefined) {
            data[treeName] = selectTenantTree(data[treeName], tid)
        }
    }
    const pseudoState = {data}
    return {
        ...exportedData,
        data: _.mapValues(data, (value) => fillConnections(value, pseudoState)),
    }
}

export const toastifySeverityMap = Object.freeze({
    INFO: toast.info,
    DEBUG: toast.info,
    WARN: toast.warning,
    ERROR: toast.error,
    FATAL: toast.error,
})

export const uwpToastIdMap = {
    UPLOAD_SUCCESS: "toast_uploadSuccess",
    GROUP_EXISTS: "toast_gruppeExistiert",
    PRODUCT_SELECTED: "toast_choseProduct",
    SAVED_ANBAHNUNG: "toast_savedAnbahnung",
    SAVED_BEREICH: "toast_savedBereich",
    EXCEL_PRINTED: "toast_excelSuccess",
    CALC_SUCCESS: "toast_calcSuccess",
    CALC_ERROR: "toast_calcError",
    PRINT_SUCCESS: "toast_printSuccess",
    PDF_NOT_FOUND: "toast_pdfNotFoundError",
    LOAD_ERROR: "toast_loadError",
    DOWNLOAD_ERROR: "toast_downloadError",
    FETCH_SUCCESS: "toast_fetchSuccess",
}

export const getValueByActiveTab = (getDataFromPointer, key) =>
    getDataFromPointer("/appstate/activeTab") === "Kunde"
        ? getDataFromPointer(`/kunde/${key}`)
        : getDataFromPointer(`/ehepartner/${key}`)

export const parseStringToNumber = (stringNumber) => stringNumber ? Number.parseInt(stringNumber) : 0

export const ROOT_DIALOG_IDENTIFIER_MAP = {
    RIESTER_VS_AV_DEPOT: "RIESTER_VS_AV_DEPOT",
    STARTEN_ODER_WARTEN: "STARTEN_ODER_WARTEN",
    AV_DETAILS: "AV_DETAILS",
}